import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { usePrescribeIntentMutation } from '../store';

type LauncherState = 'launching' | 'missing_fields' | 'error';

export function PatientPrescribeLauncher() {
  const { patientId } = useParams<{ patientId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { jotrx } = useEventTracking();
  const [prescribeIntent] = usePrescribeIntentMutation();
  const hasStartedRef = useRef(false);
  const [state, setState] = useState<LauncherState>('launching');
  const [missingFields, setMissingFields] = useState<string[]>([]);

  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    if (!patientId) {
      setState('error');
      return;
    }

    const onBehalfOfProviderId =
      searchParams.get('on_behalf_of_provider_id') || undefined;
    const source = searchParams.get('source') || 'patient_detail';

    void prescribeIntent({
      patientId,
      prescribeIntentRequest: {
        ...(onBehalfOfProviderId
          ? { on_behalf_of_provider_id: onBehalfOfProviderId }
          : {}),
      },
    })
      .unwrap()
      .then(result => {
        if (result.status === 'ready' && result.sso_url) {
          jotrx.prescribeOpened({ source, patient_id: patientId });
          window.location.replace(result.sso_url);
          return;
        }
        setMissingFields(result.missing_required_fields ?? []);
        setState('missing_fields');
      })
      .catch(() => setState('error'));
  }, [jotrx, patientId, prescribeIntent, searchParams]);

  const returnToChart = () => {
    if (patientId) {
      navigate(`/patient/${patientId}`);
      return;
    }
    navigate('/patients/');
  };

  return (
    <Box
      sx={{
        minHeight: '100%',
        display: 'grid',
        placeItems: 'center',
        px: 3,
        py: 6,
      }}
    >
      <Stack
        spacing={2}
        sx={{ maxWidth: 420, textAlign: 'center', alignItems: 'center' }}
      >
        {state === 'launching' && (
          <>
            <CircularProgress size={28} />
            <Typography variant="h6">Opening DoseSpot</Typography>
            <Typography variant="body2" color="text.secondary">
              Preparing the prescription workspace.
            </Typography>
          </>
        )}

        {state === 'missing_fields' && (
          <>
            <Typography variant="h6">More information is needed</Typography>
            <Typography variant="body2" color="text.secondary">
              Return to the chart and complete the e-prescription details.
            </Typography>
            {missingFields.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                Missing: {missingFields.join(', ')}
              </Typography>
            )}
            <Button variant="contained" onClick={returnToChart}>
              Back to Chart
            </Button>
          </>
        )}

        {state === 'error' && (
          <>
            <Typography variant="h6">DoseSpot did not open</Typography>
            <Typography variant="body2" color="text.secondary">
              Return to JotPsych and try again.
            </Typography>
            <Button variant="contained" onClick={returnToChart}>
              Back to Chart
            </Button>
          </>
        )}
      </Stack>
    </Box>
  );
}
